/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import { Box, Paper, Group, Image } from '@mantine/core';

// styles
import classes from './producers.module.css';

// components
import { Pagination } from '@components/atoms/Pagination';
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';

// i18n
import { useTranslation } from 'react-i18next';

// icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// api
import { useProducersApi } from '@features/resources/producers/api/get-producers';
import { useRemove } from '@features/resources/producers/api/remove-producer';

// configs
import { tableSchemaBase as columns } from '@features/resources/producers/producers.columns';
import { tableSchemaBase as columnsWithoutAdmin } from '@features/resources/producers/producersWithoutContact.columns';

// utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';
import { TParsedProducer } from '@features/resources/producers/types';
import { useProducers } from '@features/resources/producers/hooks/useProducers';

import { TTargetColumn } from '@global-types/index';
import { DrawerProducer } from '@features/resources/producers/components/DrawerProducer';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import isNil from 'lodash/isNil';
import { AddProducer } from '@features/resources/producers/components/AddProducer';
import { VERIFIED_FILTER_OPTIONS } from '@utils/options';
import { useOrganizationsHasProducers } from 'shared/api/get-organizations-has-entity-producer';
import { EditProducer } from '@features/resources/producers/components/EditProducer';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useProjectKpis } from '@features/resources/producers/api/project-kpis';
import { useEffect } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Producers = () => {
	const { t } = useTranslation();

	const {
		showScrollFilters,
		openDrawerAddProducer,
		setOpenDrawerAddProducer,
		handleAddItem,
		ref,
		producersDatatableOptions,
		producersWithoutContactDatatableOptions,
		setProducersDatatableOptions,
		containerRef,
		handleScroll,
		handleOrganizationFilter,
		openDrawer,
		setOpenDrawer,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		handleOpenConfirmationDeleteItem,
		handleViewMore,
		handleRowClicked,
		handleSelectedRecordsChange,
		handleSortStatusChange,
		isOverflowFilters,
		handlepaginationchangesize,
		handlechangepagination,
		producersPage,
		producersPageSize,
		producersOpenConfirmationModalDelete,
		setProducersOpenConfirmationModalDelete,
		producersQueryParams,
		producersSortStatus,
		producersSortParams,
		producersSelectedRow,
		handleCountryFilter,
		handleapplystatusfilter,
		openDrawerEditProducer,
		handleCloseEditProducerDrawer,
		handleEditProducerDrawer,
		handleEdit,
		setProducersWithoutContactDatatableOptions,
	} = useProducers();

	// APIs
	const { data: organizations } =
		useOrganizationsHasProducers();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useProducersApi(
		producersPage,
		producersPageSize,
		producersQueryParams,
		producersSortParams
	);

	const remove = useRemove(
		producersPage,
		producersPageSize
	);

	const { data: countryCodes } =
		useCountryCodes('producers');
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { checkAcl, checkAccess, checkRole } =
		useAuthorization();

	const {
		data: statisticsProjectKpis,
		mutate: statisticsProjectKpisMutation,
	} = useProjectKpis({
		mutationConfig: {},
	});

	useEffect(() => {
		if (
			checkRole([
				ROLES.PRODUCER_ADMIN,
				ROLES.PRODUCER_STAFF,
				ROLES.ORGANIZATION_ADMIN,
				ROLES.ORGANIZATION_STAFF,
			])
		) {
			const newQueryParams = {
				ids: [],
				kpis: [
					'SUM_OFFSET_AMOUNT_GCO2',
					'SUM_OFFSET_AMOUNT_EURO',
				],
			};

			statisticsProjectKpisMutation(newQueryParams);
		}
	}, []);

	return (
		<Box className={classes.container} ref={ref}>
			{/*Drawer producer */}
			<AddProducer
				opened={openDrawerAddProducer}
				onClose={() => {
					setOpenDrawerAddProducer(false);
				}}
			/>

			{/*Drawer edit brand */}
			<EditProducer
				opened={openDrawerEditProducer}
				onClose={handleCloseEditProducerDrawer}
			/>

			{/*Drawer show more producer */}
			<DrawerProducer
				opened={openDrawer}
				handledelete={handleOpenConfirmationDeleteItem}
				onClose={() => {
					setOpenDrawer(false);
				}}
				data={producersSelectedRow}
				handleupdate={handleEditProducerDrawer}
				kpisProjects={
					statisticsProjectKpis?.projects || []
				}
			/>

			{/*Delete confirmation modal */}
			<ConfirmationModal
				title={t(
					'producers.alert.confirmationDelete.title'
				)}
				description={t(
					'producers.alert.confirmationDelete.description'
				)}
				onConfirm={() => {
					handleConfirmDeleteItem(remove);
				}}
				onClose={handleCloseConfirmationDeleteItem}
				opened={
					producersOpenConfirmationModalDelete &&
					producersOpenConfirmationModalDelete.open
				}
			/>

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleOrganizationFilter}
												label="organizations"
												size="lg"
												options={convertArrayToLabelValue(
													organizations
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchOrganization'
												)}
												placeholder={t(
													'filters.placeholders.selectOrganization'
												)}
											/>
										</Box>
									</Authorization>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											value={
												producersQueryParams[
													'location.countryCode'
												]
													? producersQueryParams[
															'location.countryCode'
														]['$in']
													: []
											}
											isPersist
											onApply={handleCountryFilter}
											label={t('global.country')}
											size="lg"
											options={filteredCountries}
											placeholderSearch={t(
												'filters.placeholderSearch.searchCountry'
											)}
											placeholder={t(
												'filters.placeholders.selectCountry'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={handleapplystatusfilter}
											label={t('global.status')}
											size="lg"
											options={VERIFIED_FILTER_OPTIONS}
											placeholderSearch={t(
												'filters.placeholderSearch.searchStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterNumeric
											}
										>
											<AddFiltersButton>
												{t('global.addFilters')}
											</AddFiltersButton>
										</Box>
									</Authorization>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.PRODUCERS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={handleAddItem}
								>
									{t('producers.addProducer')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={handleAddItem}
								>
									{t('producers.addProducer')}
								</Button>
							</div>
						)}
				</Box>

				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? setProducersDatatableOptions(
										getVisibleColumns(columns, cols)
									)
								: setProducersWithoutContactDatatableOptions(
										getVisibleColumns(
											columnsWithoutAdmin,
											cols
										)
									);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? columns
								: columnsWithoutAdmin
						}
						columns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? producersDatatableOptions
								: producersWithoutContactDatatableOptions
						}
						data={data}
						onRowClick={handleRowClicked}
						onEditItem={handleEdit}
						onViewMore={handleViewMore}
						onRemoveItem={(row: TParsedProducer) => {
							setProducersOpenConfirmationModalDelete({
								open: true,
								params: row,
							});
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						onSelectedRecordsChange={
							handleSelectedRecordsChange
						}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={producersSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={producersPageSize}
					totalitems={totalItems}
					activepage={producersPage}
					handlechangesize={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Producers;
